/**
 * Resolving a "preselect this plan" request against the plans an org can
 * actually buy.
 *
 * Callers across the app open the upgrade modal pointing at a specific plan --
 * the next tier up from a limit warning, `growth-plan-monthly` from the power
 * dialer's upgrade panel. The modal had a `preselectedPlanId` prop for this and
 * never read it, so every one of those CTAs dropped the user on the generic
 * plan grid with no indication of which plan they had just asked for.
 *
 * Matching is by id ONLY, and that restraint is the point.
 * `/api/billing/upgrade-context` returns just the ladder the org's brand may
 * buy from (server/utils/planCatalog.ts), and the same tier NAME means a
 * different product at a different price in each ladder: "Growth" is $49
 * virtual-phone, $149 AI-receptionist, $79 callback. Falling back to a name
 * match when the id is absent would preselect a plan from another product --
 * for the power dialer specifically, one that does not include the power
 * dialer. An unmatched id therefore leaves the plan grid alone: no preselect is
 * a worse CTA than the right one, and a better outcome than the wrong purchase.
 */

export interface PreselectablePlan {
  id: string;
  name: string;
  billingInterval: string;
}

export type BillingIntervalToggle = 'monthly' | 'yearly';

export interface PreselectMatch<T extends PreselectablePlan> {
  plan: T;
  /** Which side of the monthly/yearly toggle shows this plan. */
  interval: BillingIntervalToggle;
}

function toggleFor(plan: PreselectablePlan): BillingIntervalToggle {
  return plan.billingInterval === 'yearly' ? 'yearly' : 'monthly';
}

export function resolvePreselectedPlan<T extends PreselectablePlan>(
  plans: readonly T[] | undefined | null,
  preselectedPlanId: string | undefined | null,
): PreselectMatch<T> | null {
  const wanted = preselectedPlanId?.trim();
  if (!wanted || !plans?.length) return null;

  const plan =
    plans.find((candidate) => candidate.id === wanted) ??
    plans.find((candidate) => candidate.id.toLowerCase() === wanted.toLowerCase());

  return plan ? { plan, interval: toggleFor(plan) } : null;
}
